/**
 * @module NotificationMutations
 * @package @geenius/notifications-convex
 * @description Declares publishable Convex mutations for creating, updating,
 * and deleting notifications plus managing persisted delivery preferences.
 */

import { v } from 'convex/values'
import type { Id } from './_generated/dataModel.js'
import { mutation } from './_generated/server.js'

const notificationChannelValidator = v.union(
  v.literal('in-app'),
  v.literal('email'),
  v.literal('push'),
  v.literal('sms'),
)

const notificationTypeValidator = v.union(
  v.literal('info'),
  v.literal('success'),
  v.literal('warning'),
  v.literal('error'),
  v.literal('mention'),
  v.literal('invite'),
  v.literal('payment'),
  v.literal('system'),
)

const notificationMetadataValidator = v.record(
  v.string(),
  v.union(v.string(), v.number(), v.boolean(), v.null()),
)

const notificationChannelsPreferencesValidator = v.object({
  'in-app': v.boolean(),
  email: v.boolean(),
  push: v.boolean(),
  sms: v.boolean(),
})

const notificationTypesPreferencesValidator = v.object({
  info: v.boolean(),
  success: v.boolean(),
  warning: v.boolean(),
  error: v.boolean(),
  mention: v.boolean(),
  invite: v.boolean(),
  payment: v.boolean(),
  system: v.boolean(),
})

/**
 * Creates a single notification for a user.
 *
 * @param args Notification data to insert.
 * @returns The inserted notification document identifier.
 */
export const createNotification = mutation({
  args: {
    userId: v.string(),
    type: notificationTypeValidator,
    title: v.string(),
    body: v.string(),
    channel: v.optional(notificationChannelValidator),
    actorId: v.optional(v.string()),
    actorName: v.optional(v.string()),
    actorAvatar: v.optional(v.string()),
    resourceType: v.optional(v.string()),
    resourceId: v.optional(v.string()),
    resourceUrl: v.optional(v.string()),
    actionLabel: v.optional(v.string()),
    actionUrl: v.optional(v.string()),
    metadata: v.optional(notificationMetadataValidator),
  },
  handler: async (ctx, args) => {
    return ctx.db.insert('notifications', {
      ...args,
      channel: args.channel ?? 'in-app',
      status: 'unread',
      createdAt: new Date().toISOString(),
    })
  },
})

/**
 * Marks a single notification as read.
 *
 * @param args.notificationId Notification document identifier.
 * @returns Nothing.
 */
export const markRead = mutation({
  args: { notificationId: v.id('notifications') },
  handler: async (ctx, args) => {
    await ctx.db.patch(args.notificationId, {
      status: 'read',
      readAt: new Date().toISOString(),
    })
  },
})

/**
 * Marks every unread notification for a user as read.
 *
 * @param args.userId Target user identifier.
 * @returns Nothing.
 */
export const markAllRead = mutation({
  args: { userId: v.string() },
  handler: async (ctx, args) => {
    const notifications = await ctx.db
      .query('notifications')
      .withIndex('by_userId_createdAt', (q) => q.eq('userId', args.userId))
      .collect()
    const unread = notifications.filter(
      (notification) => notification.status === 'unread',
    )
    const now = new Date().toISOString()
    for (const notification of unread) {
      await ctx.db.patch(notification._id, { status: 'read', readAt: now })
    }
  },
})

/**
 * Dismisses a notification without deleting it.
 *
 * @param args.notificationId Notification document identifier.
 * @returns Nothing.
 */
export const dismissNotification = mutation({
  args: { notificationId: v.id('notifications') },
  handler: async (ctx, args) => {
    await ctx.db.patch(args.notificationId, { status: 'dismissed' })
  },
})

/**
 * Permanently deletes a notification.
 *
 * @param args.notificationId Notification document identifier.
 * @returns Nothing.
 */
export const deleteNotification = mutation({
  args: { notificationId: v.id('notifications') },
  handler: async (ctx, args) => {
    await ctx.db.delete(args.notificationId)
  },
})

/**
 * Creates or updates notification preferences for a user.
 *
 * @param args Preference updates to persist.
 * @returns Nothing.
 */
export const updatePreferences = mutation({
  args: {
    userId: v.string(),
    channels: v.optional(notificationChannelsPreferencesValidator),
    types: v.optional(notificationTypesPreferencesValidator),
    quietHours: v.optional(v.object({ start: v.string(), end: v.string() })),
    timezone: v.optional(v.string()),
  },
  handler: async (ctx, args) => {
    const existing = await ctx.db.query('notification_preferences')
      .withIndex('by_userId', (q) => q.eq('userId', args.userId))
      .first()
    if (existing) {
      const updates: {
        channels?: typeof args.channels
        types?: typeof args.types
        quietHours?: typeof args.quietHours
        timezone?: typeof args.timezone
      } = {}

      if (args.channels) {
        updates.channels = args.channels
      }
      if (args.types) {
        updates.types = args.types
      }
      if (args.quietHours) {
        updates.quietHours = args.quietHours
      }
      if (args.timezone) {
        updates.timezone = args.timezone
      }

      await ctx.db.patch(existing._id, updates)
    } else {
      await ctx.db.insert('notification_preferences', {
        userId: args.userId,
        channels: args.channels ?? {
          'in-app': true,
          email: true,
          push: false,
          sms: false,
        },
        types: args.types ?? {
          info: true,
          success: true,
          warning: true,
          error: true,
          mention: true,
          invite: true,
          payment: true,
          system: true,
        },
        quietHours: args.quietHours,
        timezone: args.timezone,
      })
    }
  },
})

/**
 * Creates multiple notifications in a single mutation.
 *
 * @param args.items Notification records to insert.
 * @returns Identifiers for the inserted notifications.
 */
export const bulkCreateNotifications = mutation({
  args: {
    items: v.array(v.object({
      userId: v.string(),
      type: notificationTypeValidator,
      title: v.string(),
      body: v.string(),
      channel: v.optional(notificationChannelValidator),
      actorId: v.optional(v.string()),
      actorName: v.optional(v.string()),
      actorAvatar: v.optional(v.string()),
      resourceType: v.optional(v.string()),
      resourceId: v.optional(v.string()),
      resourceUrl: v.optional(v.string()),
      actionLabel: v.optional(v.string()),
      actionUrl: v.optional(v.string()),
      metadata: v.optional(notificationMetadataValidator),
    })),
  },
  handler: async (ctx, args) => {
    const now = new Date().toISOString()
    const ids: Array<Id<'notifications'>> = []

    for (const item of args.items) {
      const id = await ctx.db.insert('notifications', {
        ...item,
        channel: item.channel ?? 'in-app',
        status: 'unread',
        createdAt: now,
      })
      ids.push(id)
    }

    return ids
  },
})
